const STOP_CITY_WORDS = new Set([
  "aujourd hui",
  "demain",
  "ce soir",
  "samedi",
  "dimanche",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "chez moi",
  "cinq",
  "quatre",
  "trois",
  "deux",
]);

const NUMBER_WORDS = {
  deux: 2,
  trois: 3,
  quatre: 4,
  cinq: 5,
  six: 6,
  sept: 7,
  huit: 8,
  neuf: 9,
  dix: 10,
};

const CITY_TRAILING_TOKENS = new Set([
  "aujourd",
  "demain",
  "ce",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
  "dimanche",
]);

const SPORT_TERMS = [
  ["course a pied", "course"],
  ["arts martiaux", "arts martiaux"],
  ["football", "football"],
  ["randonnee", "randonnée"],
  ["natation", "natation"],
  ["fitness", "fitness"],
  ["yoga", "yoga"],
  ["velo", "vélo"],
];

export function normalizeConciergeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, " ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function includesAny(text, terms) {
  return terms.some((term) => text.includes(normalizeConciergeText(term)));
}

function action(id, label, route, description, options = {}) {
  return {
    id,
    label,
    route,
    description,
    kind: options.kind || "open",
    requiresAuth: Boolean(options.requiresAuth),
    requiresConfirmation: Boolean(options.requiresConfirmation),
    confirmation: options.confirmation || "",
  };
}

function route(path, values = {}) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    const normalized = String(value ?? "").trim();
    if (normalized) params.set(key, normalized);
  });
  params.set("concierge", "1");
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function detectedDate(text) {
  const candidates = [
    ["aujourd hui", "Aujourd’hui"],
    ["demain", "Demain"],
    ["ce soir", "Ce soir"],
    ["lundi", "Lundi"],
    ["mardi", "Mardi"],
    ["mercredi", "Mercredi"],
    ["jeudi", "Jeudi"],
    ["vendredi", "Vendredi"],
    ["samedi", "Samedi"],
    ["dimanche", "Dimanche"],
  ];
  return candidates.find(([needle]) => text.includes(needle))?.[1] || "";
}

function detectedCapacity(text) {
  const numeric = text.match(/(?:a|pour|avec)\s+(\d{1,2})(?:\s+personnes?)?/);
  if (numeric) return Math.max(1, Math.min(20, Number(numeric[1])));
  for (const [word, value] of Object.entries(NUMBER_WORDS)) {
    if (new RegExp(`(?:a|pour|avec)\\s+${word}\\b`).test(text)) return value;
  }
  return "";
}

function detectedCity(text) {
  const match = text.match(/(?:^|\s)(?:pres de|autour de|a cote de|sur|a)\s+([a-z-]+(?:\s+[a-z-]+)?)/);
  if (!match) return "";
  const words = String(match[1] || "").trim().split(" ");
  if (words.length > 1 && CITY_TRAILING_TOKENS.has(words[words.length - 1])) words.pop();
  const city = words.join(" ");
  if (!city || STOP_CITY_WORDS.has(city)) return "";
  return city.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function detectedSportTerm(text) {
  return SPORT_TERMS.find(([needle]) => text.includes(needle))?.[1] || "";
}

function factsFrom(text) {
  return {
    date: detectedDate(text),
    city: detectedCity(text),
    capacity: detectedCapacity(text),
    sport: detectedSportTerm(text),
  };
}

function factLabels(facts) {
  return [
    facts.city ? `Lieu repéré : ${facts.city}` : "",
    facts.date ? `Moment repéré : ${facts.date}` : "",
    facts.capacity ? `${facts.capacity} personne${facts.capacity > 1 ? "s" : ""}` : "",
  ].filter(Boolean);
}

function sportPlan(query, facts, organizing) {
  if (organizing) {
    return {
      id: "sport_organize",
      title: "Organiser une activité gratuite",
      summary: "Le Concierge prépare le vrai formulaire Sport & loisirs. Rien n’est publié sans votre validation dans le formulaire.",
      actions: [
        action(
          "sport_prepare",
          "Préparer l’activité",
          route("/market/sport-loisir", {
            tab: "create",
            pricing: "free",
            title: query,
            city: facts.city,
            capacity: facts.capacity,
          }),
          "Ouvrir le formulaire gratuit prérempli.",
          {
            kind: "prepare",
            requiresAuth: true,
            requiresConfirmation: true,
            confirmation: "Ouvrir le formulaire Sport & loisirs ? Aucune activité ne sera publiée automatiquement.",
          }
        ),
        action("sport_agenda", "Voir mon agenda", route("/mes-eternua/agenda", { category: "sport_activity" }), "Vérifier le créneau et les rappels.", { requiresAuth: true }),
        action("sport_messages", "Préparer les invitations", route("/amis/salon", { context: "sport" }), "Retrouver la messagerie avant d’inviter les participants.", { requiresAuth: true }),
      ],
    };
  }
  return {
    id: "sport_discover",
    title: "Trouver une activité gratuite",
    summary: "Le Concierge ouvre les activités Sport & loisirs réellement disponibles, avec le filtre gratuit appliqué.",
    actions: [
      action("sport_search", "Voir les activités gratuites", route("/market/sport-loisir", { tab: "explore", pricing: "free", q: facts.sport, city: facts.city }), "Rechercher dans le catalogue Sport & loisirs."),
      action("sport_map", "Voir sur la carte", route("/carte-vivante", { q: query, city: facts.city, category: "sport" }), "Afficher les résultats publics géolocalisés."),
      action("sport_agenda", "Voir mon agenda", route("/mes-eternua/agenda", { category: "sport_activity" }), "Retrouver les activités réservées et leurs rappels.", { requiresAuth: true }),
    ],
  };
}

function sellingPlan(query) {
  return {
    id: "sell",
    title: "Vendre un objet",
    summary: "Le Concierge vous conduit vers le formulaire de vente existant. La publication reste sous votre contrôle.",
    actions: [
      action("sell_prepare", "Préparer l’annonce", route("/market/vendre", { tab: "publier", title: query }), "Ouvrir un brouillon d’annonce.", {
        kind: "prepare",
        requiresAuth: true,
        requiresConfirmation: true,
        confirmation: "Ouvrir le formulaire de vente ? Aucune annonce ne sera publiée automatiquement.",
      }),
      action("sell_compare", "Comparer les annonces", route("/recherche", { q: query, universe: "marketplace" }), "Consulter les offres publiques proches de votre demande."),
      action("sell_manage", "Retrouver mes contenus", route("/mes-contenus", { type: "market" }), "Continuer un brouillon existant.", { requiresAuth: true }),
    ],
  };
}

function babysittingPlan(query, facts) {
  return {
    id: "babysitting",
    title: "Trouver une garde de confiance",
    summary: "La recherche, le passeport de confiance et la demande restent trois étapes distinctes. Aucune donnée concernant un enfant n’est reprise par le Concierge.",
    actions: [
      action("babysitting_search", "Rechercher une personne", route("/market/babysitting", { q: query, city: facts.city, date: facts.date }), "Ouvrir la recherche Babysitting sécurisée.", { requiresAuth: true }),
      action("babysitting_trust", "Vérifier la confiance", "/passeport-confiance", "Consulter les preuves et explications disponibles.", { requiresAuth: true }),
      action("babysitting_request", "Préparer une demande", route("/market/babysitting/demandes/nouvelle", { mode: "parent" }), "Ouvrir le formulaire protégé sans le publier.", {
        kind: "prepare",
        requiresAuth: true,
        requiresConfirmation: true,
        confirmation: "Ouvrir le formulaire protégé Babysitting ? Ne saisissez les informations privées que dans ce formulaire dédié.",
      }),
    ],
  };
}

function outingPlan(query, facts) {
  return {
    id: "outing_organize",
    title: "Organiser une sortie",
    summary: "Le Concierge prépare les trois outils utiles : publication, agenda et invitations. Il n’envoie rien automatiquement.",
    actions: [
      action("outing_prepare", "Préparer la sortie", route("/market/sortie", { tab: "create", title: query, city: facts.city, capacity: facts.capacity, date: facts.date }), "Ouvrir le formulaire Sorties.", {
        kind: "prepare",
        requiresAuth: true,
        requiresConfirmation: true,
        confirmation: "Ouvrir le formulaire Sorties ? La création restera à confirmer dans l’espace dédié.",
      }),
      action("outing_agenda", "Choisir le créneau", route("/mes-eternua/agenda/nouveau", { category: "sortie_event", title: query }), "Préparer un événement personnel.", { requiresAuth: true }),
      action("outing_invite", "Ouvrir les messages", route("/amis/salon", { context: "sortie" }), "Contacter les personnes après avoir vérifié le projet.", { requiresAuth: true }),
    ],
  };
}

function servicesPlan(query, facts) {
  return {
    id: "local_help",
    title: "Trouver un coup de main près de chez vous",
    summary: "Le Concierge rapproche votre besoin des services publics disponibles, sans inventer de prestataire ni de niveau de confiance.",
    actions: [
      action("service_search", "Rechercher un service", route("/market/services", { tab: "explorer", q: query, city: facts.city }), "Voir les offres réellement publiées."),
      action("service_map", "Voir les services sur la carte", route("/carte-vivante", { q: query, city: facts.city, category: "services" }), "Explorer les résultats publics locaux."),
      action("service_request", "Préparer ma demande", route("/market/services", { tab: "proposer", view: "publish", listing_type: "request", title: query, city: facts.city }), "Ouvrir un brouillon de demande.", {
        kind: "prepare",
        requiresAuth: true,
        requiresConfirmation: true,
        confirmation: "Ouvrir le formulaire de demande de service ? Rien ne sera publié automatiquement.",
      }),
    ],
  };
}

function simplePlan(id, title, summary, actions) {
  return { id, title, summary, actions };
}

export function buildConciergePlan(value) {
  const query = String(value || "").trim().slice(0, 240);
  const text = normalizeConciergeText(query);
  const facts = factsFrom(text);
  const organizing = includesAny(text, ["organiser", "organise", "organisons", "creer", "publier", "proposer"]);
  let plan;

  if (includesAny(text, ["nounou", "baby sitter", "babysitting", "garde d enfant", "garder mon enfant", "garder mes enfants"])) {
    plan = babysittingPlan(query, facts);
  } else if (includesAny(text, ["vendre", "je vends", "mettre en vente", "annonce de vente"])) {
    plan = sellingPlan(query);
  } else if (includesAny(text, ["sortie a", "organiser une sortie", "organise une sortie", "inviter pour une sortie"])) {
    plan = outingPlan(query, facts);
  } else if (includesAny(text, ["sport", "activite sportive", "football", "randonnee", "yoga", "natation", "fitness", "velo", "course a pied"])) {
    plan = sportPlan(query, facts, organizing);
  } else if (includesAny(text, ["coup de main", "plombier", "bricolage", "menage", "jardinage", "aide a domicile", "besoin d un service", "chercher un service"])) {
    plan = servicesPlan(query, facts);
  } else if (includesAny(text, ["emploi", "travail", "offre d emploi", "recrutement", "stage", "alternance"])) {
    plan = simplePlan("employment", "Avancer dans l’emploi", "Le Concierge ouvre les offres, vos démarches et votre profil sans déposer de candidature automatiquement.", [
      action("employment_search", "Rechercher une offre", route("/market/emploi", { tab: "offres", q: query, city: facts.city }), "Consulter les offres disponibles."),
      action("employment_journeys", "Voir mes candidatures", route("/mes-eternua/demarches", { type: "employment" }), "Continuer une démarche existante.", { requiresAuth: true }),
      action("employment_profile", "Compléter mon profil", "/profil/rubrique/emploi", "Mettre à jour votre présentation professionnelle.", { requiresAuth: true }),
    ]);
  } else if (includesAny(text, ["agenda", "rendez vous", "rappel", "planning", "calendrier"])) {
    plan = simplePlan("agenda", "Organiser votre temps", "Le Concierge ouvre les outils Agenda existants. Aucun rendez-vous n’est créé à partir de votre phrase.", [
      action("agenda_open", "Ouvrir mon agenda", "/mes-eternua/agenda", "Voir vos prochains événements.", { requiresAuth: true }),
      action("agenda_prepare", "Préparer un événement", route("/mes-eternua/agenda/nouveau", { title: query }), "Ouvrir le formulaire Agenda.", { requiresAuth: true, requiresConfirmation: true, confirmation: "Ouvrir le formulaire Agenda ? L’événement restera à confirmer." }),
      action("agenda_preferences", "Régler les rappels", "/mes-eternua/agenda/preferences", "Gérer vos préférences de rappel.", { requiresAuth: true }),
    ]);
  } else if (includesAny(text, ["message", "repondre", "conversation", "contacter", "ecrire a"])) {
    plan = simplePlan("messages", "Retrouver vos échanges", "Le Concierge ouvre la messagerie privée. Il ne lit ni n’envoie aucun message.", [
      action("messages_open", "Ouvrir mes messages", "/amis/salon", "Voir les conversations privées.", { requiresAuth: true }),
      action("notifications_open", "Voir les notifications", "/notifications", "Consulter les alertes disponibles.", { requiresAuth: true }),
      action("trusted_contacts", "Voir mes proches de confiance", "/mes-eternua/proches-de-confiance", "Retrouver votre réseau autorisé.", { requiresAuth: true }),
    ]);
  } else if (includesAny(text, ["confidentialite", "mes donnees", "consentement", "session", "securite", "confiance"])) {
    plan = simplePlan("privacy_trust", "Contrôler vos données et votre confiance", "Le Concierge vous conduit vers les réglages existants sans modifier votre compte.", [
      action("trust_open", "Ouvrir mon passeport", "/passeport-confiance", "Consulter les éléments de confiance.", { requiresAuth: true }),
      action("privacy_open", "Voir la confidentialité", "/confiance-confidentialite", "Consulter les contrôles actuellement disponibles.", { requiresAuth: true }),
      action("security_open", "Renforcer mon compte", "/mes-eternua/protection", "Voir les contrôles de sécurité.", { requiresAuth: true }),
    ]);
  } else {
    plan = simplePlan("search", query ? "Explorer Eternua" : "Choisir un besoin", query
      ? "Votre demande ne correspond pas encore à un parcours spécialisé. Le Concierge conserve vos mots pour la recherche générale."
      : "Décrivez simplement votre besoin ou choisissez un exemple. Les règles locales proposeront au maximum trois chemins.", [
      action("global_search", "Rechercher dans Eternua", route("/recherche", { q: query }), "Chercher dans les contenus publics disponibles."),
      action("global_map", "Explorer la carte", route("/carte-vivante", { q: query }), "Voir les contenus publics géolocalisés."),
      action("global_publish", "Choisir quoi publier", "/publier", "Ouvrir le point d’entrée de publication.", { requiresAuth: true }),
    ]);
  }

  return {
    ...plan,
    query,
    facts,
    factLabels: factLabels(facts),
    processing: "local_rules",
    confidence: plan.id === "search" ? "fallback" : "matched",
  };
}

export function buildTodayActions(priorities = [], translateLabel = (label) => label) {
  const rows = Array.isArray(priorities) ? priorities : [];
  const message = rows.find((item) => item?.id === "messages-unread" || item?.universe === "Messages");
  const continuation = rows.find((item) => item !== message && item?.action?.url);
  return [
    {
      id: "continue",
      eyebrow: translateLabel("Continuer"),
      title: continuation?.title || translateLabel("Reprendre une démarche"),
      description: continuation?.summary || translateLabel("Retrouvez vos publications, réservations et démarches en cours."),
      label: continuation?.action?.label || translateLabel("Voir mes démarches"),
      route: continuation?.action?.url || "/mes-eternua/demarches",
      icon: "↻",
    },
    {
      id: "discover",
      eyebrow: translateLabel("Découvrir"),
      title: translateLabel("Une activité gratuite près de vous"),
      description: translateLabel("Explorez le pilote Sport & loisirs sans paiement réel."),
      label: translateLabel("Voir les activités"),
      route: "/market/sport-loisir?tab=explore&pricing=free&concierge=1",
      icon: "◎",
    },
    {
      id: "respond",
      eyebrow: translateLabel("Répondre"),
      title: message?.title || translateLabel("Vos messages et invitations"),
      description: message?.summary || translateLabel("Retrouvez les personnes qui attendent votre réponse."),
      label: message?.action?.label || translateLabel("Ouvrir les messages"),
      route: message?.action?.url || "/amis/salon",
      icon: "▱",
    },
  ];
}

export function safeConciergeRoute(value) {
  const candidate = String(value || "");
  return candidate.startsWith("/") && !candidate.startsWith("//") ? candidate : "/";
}
